/*
 * Pure helpers for the inbox message component (<InboxMessage>). No Preact / no
 * store dependencies, so these are unit-testable in isolation with webapp Jest.
 */

// Window (ms) in which a second tap on the same bubble counts as a double-tap.
export const TAP_THRESHOLD_MS = 250;

// True when `now` falls within `threshold` ms after the previous tap. A falsy
// `prevTapAt` (0 / null / undefined) means there is no pending tap, so it is
// never a double-tap.
export function isDoubleTap(prevTapAt, now, threshold = TAP_THRESHOLD_MS) {
    if (!prevTapAt) {
        return false;
    }
    return now - prevTapAt <= threshold;
}

// Ordered list of action keys the details strip shows for a message. `has` lets
// the caller hide an action whose handler was not supplied (e.g. the full-page
// inbox has no reply). An action is shown unless its key is explicitly `false`.
export function messageActions({ isOwnMessage = false, isBroadcast = false, has = {} } = {}) {
    const show = key => has[key] !== false;
    const actions = [];

    if (isBroadcast && !isOwnMessage) {
        if (show('replyToUser')) {
            actions.push('replyToUser');
        }
    } else if (!isBroadcast && show('reply')) {
        // Non-broadcast messages: reply is available unless the caller suppresses
        // it via has.reply === false (the component does this for own messages).
        actions.push('reply');
    }

    actions.push('react');

    if (show('copy')) {
        actions.push('copy');
    }
    if (isOwnMessage) {
        if (show('edit')) {
            actions.push('edit');
        }
        if (show('delete')) {
            actions.push('delete');
        }
    }
    return actions;
}

// Derives the raw read / edit / time values the details strip displays. Pure —
// the component formats `timeValue` / `readAt` with util.formatDate & getTimeAgo.
// Read status is only meaningful for your OWN messages (did the recipient read it).
export function readState(message = {}, isOwnMessage = false) {
    return {
        timeValue: message.sentDate || message.createdDate || null,
        showRead: Boolean(isOwnMessage),
        isRead: Boolean(message.readDate),
        readAt: message.readDate || null,
        isEdited: Boolean(message.editedDate),
    };
}

// --- Swipe-to-reply / long-press gesture geometry (pure) ---

// Minimum movement (px) before a drag commits to an axis.
export const SWIPE_START_PX = 10;
// Rightward drag (px) past which releasing triggers a reply.
export const SWIPE_REPLY_PX = 60;
// Max bubble translate (px) during a swipe.
export const SWIPE_MAX_PX = 90;
// Hold duration (ms) that opens the reaction picker.
export const LONG_PRESS_MS = 450;

// Which axis a drag has committed to. 'none' until either axis exceeds startPx.
// Ties (|dx| === |dy|) bias to 'vertical' so we defer to list scrolling.
export function classifySwipe(dx, dy, startPx = SWIPE_START_PX) {
    const ax = Math.abs(dx);
    const ay = Math.abs(dy);
    if (ax < startPx && ay < startPx) {
        return 'none';
    }
    return ax > ay ? 'horizontal' : 'vertical';
}

// Clamp a horizontal drag to a rightward translate in [0, maxPx]. Leftward
// drags translate 0 (swipe-right only).
export function swipeTranslateX(dx, maxPx = SWIPE_MAX_PX) {
    if (dx <= 0) {
        return 0;
    }
    return Math.min(dx, maxPx);
}

// True when a rightward drag has reached the reply threshold.
export function isReplyTriggered(dx, replyPx = SWIPE_REPLY_PX) {
    return dx >= replyPx;
}
